import React, { useState, useEffect, useRef } from "react";
import Header from "../components/Header";
import HeaderStepPractice from "../components/HeaderStepPractice";
import BoxButton from "../components/BoxButton";
import PopupError from "../components/Generics/PopupError";
import AML_FISICO from "../components/AML/AML_FISICO/AML_FISICO";
import AML_GIURIDICO from "../components/AML/AML_GIURIDICO/AML_GIURIDICO";
import { CircularProgress, Modal, Grid, Typography, Box } from "@mui/material";
import ModalError from "../components/Generics/ModalError";
import ModalConferma from "../components/Generics/ModalConferma";
import ModalConfermaAml from "../components/Generics/ModalConfermaAml";
import { clearAuthToken, service } from "../model/service";
import {
  validationAMLCliente,
  validationAMLEsecutore,
  validationAMLPrestazione,
  validationAMLTitolare,
} from "../utils/validation_aml";
import ModalSuccessAml from "../components/Generics/ModalSuccessAml";

function InvalidModal({ isIta }) {
  return (
    <>
      <div
        style={{ display: "flex", flexDirection: "column", height: "100vh" }}
      >
        <div style={{ position: "fixed", top: 0, width: "100%", zIndex: 1000 }}>
          <Header />
        </div>
      </div>
      <Modal open disableEscapeKeyDown>
        <Grid
          container
          item
          xs={11}
          md={5}
          style={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            backgroundColor: "#fff",
            maxHeight: "90%",
            borderRadius: "16px",
            zIndex: 1300,
            overflow: "auto",
            outline: "none",
          }}
        >
          <Grid
            item
            xs={12}
            sx={{
              height: 60,
              display: "flex",
              alignItems: "center",
              borderBottom: "1px solid #c6c6c6",
              px: "20px",
            }}
          >
            <Grid item xs={1.5} md={0.5}>
              <div className="icon-modal-info" />
            </Grid>
            <Grid item xs={10.5} md={11.5}>
              <Typography
                className="color-blue-dark"
                sx={{ fontSize: 18, fontWeight: 600, ml: 1 }}
              >
                {isIta ? "Accesso non autorizzato" : "Unauthorized access"}
              </Typography>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Typography
              className="color-blue-dark"
              sx={{
                padding: "50px 20px",
                fontSize: { xs: "14px", md: "16px" },
              }}
            >
              {isIta
                ? "Non è possibile accedere al modulo. Il link potrebbe essere non valido oppure la sessione è scaduta. Ricarica la pagina e riprova."
                : "Unable to access the form. The link may be invalid or your session has expired. Please reload the page and try again."}
            </Typography>
          </Grid>
        </Grid>
      </Modal>
    </>
  );
}

function MismatchModal({ isIta }) {
  return (
    <>
      <div
        style={{ display: "flex", flexDirection: "column", height: "100vh" }}
      >
        <div style={{ position: "fixed", top: 0, width: "100%", zIndex: 1000 }}>
          <Header />
        </div>
      </div>
      <Modal open disableEscapeKeyDown>
        <Grid
          container
          item
          xs={11}
          md={5}
          style={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            backgroundColor: "#fff",
            maxHeight: "90%",
            borderRadius: "16px",
            zIndex: 1300,
            overflow: "auto",
            outline: "none",
          }}
        >
          <Grid
            item
            xs={12}
            sx={{
              height: 60,
              display: "flex",
              alignItems: "center",
              borderBottom: "1px solid #c6c6c6",
              px: "20px",
            }}
          >
            <Grid item xs={1.5} md={0.5}>
              <div className="icon-modal-info" />
            </Grid>
            <Grid item xs={10.5} md={11.5}>
              <Typography
                className="color-blue-dark"
                sx={{ fontSize: 18, fontWeight: 600, ml: 1 }}
              >
                {isIta ? "Link non valido" : "Invalid link"}
              </Typography>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Typography
              className="color-blue-dark"
              sx={{
                padding: "50px 20px",
                fontSize: { xs: "14px", md: "16px" },
              }}
            >
              {isIta
                ? "Il link utilizzato non corrisponde al tipo di pratica associata. Utilizza il link originale ricevuto via email."
                : "The link used does not match the type of case associated. Please use the original link received by email."}
            </Typography>
          </Grid>
        </Grid>
      </Modal>
    </>
  );
}

const deriveUrlParamsAml = (type, language) => {
  const isGiuridico = type === "PG";
  const isIta = language === "it";
  if (isGiuridico) return isIta ? "giuridico-ita" : "giuridico-eng";
  return isIta ? "fisico-ita" : "fisico-eng";
};

const AmlPage = ({ uid, expectedType, isItaFallback }) => {
  const containerRef = useRef(null);
  const [caseData, setCaseData] = useState(null);
  const [unauthorized, setUnauthorized] = useState(false);
  const [typeMismatch, setTypeMismatch] = useState(false);
  const [loading, setLoading] = useState(true);
  const [currentStep, setCurrentStep] = useState(1);
  const [aml, setAml] = useState(null);
  const [fieldErrors, setFieldErrors] = useState([]);
  const [resetPractice, setResetPractice] = useState(false);
  const [nextStepSummary, setNextStepSummary] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [confirmAmlModal, setConfirmAmlModal] = useState(false);
  const [error, setError] = useState(false);
  const [urlParamsAml, setUrlParamsAml] = useState(null);
  const [language, setLanguage] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [successAmlModal, setSuccessAmlModal] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);

  const hasLoadedRef = useRef(false);
  const documentsRef = useRef([]);
  const amlRef = useRef(aml);
  const amlSnapshotRef = useRef(null);
  const saveTimerRef = useRef(null);

  useEffect(() => {
    amlRef.current = aml;
  }, [aml]);
  useEffect(() => {
    documentsRef.current = documents;
  }, [documents]);

  const isIta = language !== null ? language === "it" : isItaFallback;

  useEffect(() => {
    const handleExpired = () => setUnauthorized(true);
    window.addEventListener("token:expired", handleExpired);
    return () => window.removeEventListener("token:expired", handleExpired);
  }, []);

  useEffect(() => {
    const fetchForm = async () => {
      try {
        const data = await service.getAmlForm(uid);
        if (data.case.type !== expectedType) {
          setTypeMismatch(true);
          return;
        }
        setCaseData(data);
        setLanguage(data.case.language);
        const derived = deriveUrlParamsAml(data.case.type, data.case.language);
        setUrlParamsAml(derived);
        if (data.case?.step) setCurrentStep(data.case.step);
        if (data.case?.interview_data) {
          setAml(data.case.interview_data);
          amlSnapshotRef.current = JSON.stringify(data.case.interview_data);
        }
        if (data.case?.documents) setDocuments(data.case.documents);
        if (data.case?.status === "submitted") setSuccessAmlModal(true);
        if (data.case?.status === "approved" || data.case?.status === "pending_confirmation") setSuccessAmlModal(true);
        if (data.case?.status === "rejected") setCurrentStep(1);
        hasLoadedRef.current = true;
      } catch (err) {
        if (err.message === "UNAUTHORIZED") setUnauthorized(true);
        else console.error("Errore getAmlForm:", err.message);
      } finally {
        setLoading(false);
      }
    };
    if (uid) fetchForm();
  }, [uid, expectedType]);

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [currentStep]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  const handleCloseModal = () => setModalOpen(false);
  const closePopupError = () => setError(false);

  const handleAmlUpdate = async (updatedAml) => {
    setAml(updatedAml);
    if (!hasLoadedRef.current) return;
    const newSnapshot = JSON.stringify(updatedAml);
    if (newSnapshot === amlSnapshotRef.current) return;
    amlSnapshotRef.current = newSnapshot;
    const signedDoc = (documentsRef.current || []).find(
      (d) => d.type === "client_aml_signed",
    );
    if (signedDoc) {
      try {
        await service.deleteDocument(signedDoc.id);
        await refreshDocuments();
      } catch (err) {
        console.error("Errore eliminazione doc firmato:", err);
      }
    }
  };

  const handlePrevious = () => setCurrentStep((prev) => Math.max(prev - 1, 1));

  const saveAmlData = (data) => {
    const stepSnapshot = currentStep;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(async () => {
      try {
        await service.saveAmlForm(uid, {
          interview_data: data ?? amlRef.current ?? {},
          step: stepSnapshot,
        });
      } catch (err) {
        if (err.message === "UNAUTHORIZED") setUnauthorized(true);
        else console.error("Errore saveAmlData:", err.message);
      }
    }, 1500);
  };

  const saveAml = async (step, data) => {
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    try {
      await service.saveAmlForm(uid, {
        interview_data: data || amlRef.current || {},
        step,
      });
    } catch (err) {
      if (err.message === "UNAUTHORIZED") setUnauthorized(true);
      else console.error("Errore saveAml:", err.message);
    }
  };

  const sendAml = async () => {
    try {
      await service.submitAmlForm(uid, {
        interview_data: amlRef.current || {},
        step: currentStep,
      });
    } catch (err) {
      if (err.message === "UNAUTHORIZED") setUnauthorized(true);
      else console.error("Errore submitAml:", err.message);
    }
  };

  // Genera il PDF eliminando l'eventuale precedente
  const generatePdf = async () => {
    setGeneratingPdf(true);
    try {
      // Usa i documenti già in stato invece di rifare la GET
      const existingGenerated = documentsRef.current.filter(
        (d) => d.type === "generated_pdf",
      );
      if (existingGenerated.length > 0) {
        await Promise.all(
          existingGenerated.map((d) => service.deleteDocument(d.id)),
        );
      }

      await service.generateAmlDocument(caseData?.case?.id);

      // Polling con delay fisso: evita il busy-wait che martella la GET
      await new Promise((resolve, reject) => {
        const poll = async () => {
          try {
            const updated = await service.getAmlForm(uid);
            const generatedDoc = (updated?.case?.documents || []).find(
              (d) => d.type === "generated_pdf",
            );
            if (generatedDoc?.id) {
              // Aggiorna i documenti direttamente dai dati già fetchati
              if (updated?.case?.documents) setDocuments(updated.case.documents);
              resolve();
            } else {
              setTimeout(poll, 2000);
            }
          } catch (err) {
            reject(err);
          }
        };
        poll();
      });
    } finally {
      setGeneratingPdf(false);
    }
  };

  const handleNext = async () => {
    if (currentStep === 6) {
      const hasSigned = (documents || []).some(
        (d) => d.type === "client_aml_signed",
      );
      if (!hasSigned) {
        setFieldErrors(["aml_signed_missing"]);
        setError(true);
        return;
      }
      setConfirmAmlModal(true);
      return;
    }

    if (currentStep === 5) {
      setModalOpen(true);
      return;
    }

    const validationMap = {
      1: () => validationAMLCliente(aml, urlParamsAml, documents),
      2: () => validationAMLEsecutore(aml, urlParamsAml, documents),
      3: () => validationAMLTitolare(aml, urlParamsAml),
      4: () => validationAMLPrestazione(aml, urlParamsAml),
    };

    const validate = validationMap[currentStep];
    if (validate) {
      const errors = validate();
      if (errors.length > 0) {
        // Validazione fallita: salva con step corrente per non perdere i dati
        await saveAml(currentStep);
        setFieldErrors(errors);
        setError(true);
        return;
      }
    }

    // Validazione passata: salva direttamente con lo step successivo
    const nextStep = currentStep < 4
      ? (nextStepSummary ? 5 : currentStep + 1)
      : 5;
    await saveAml(nextStep);
    setCurrentStep(nextStep);
    setFieldErrors([]);
  };

  const goToStep = (step) => {
    setCurrentStep(step);
    setNextStepSummary(true);
  };
  const openModalResetPractice = () => setResetPractice(true);
  const handleCloseResetPracticeModal = () => setResetPractice(false);

  const startAgain = async () => {
    setResetPractice(false);
    setCurrentStep(1);
    setAml({});
  };

  const sendFormAml = async () => {
    await sendAml();
  };

  const refreshDocuments = async () => {
    const data = await service.getAmlForm(uid);
    if (data.case?.documents) setDocuments(data.case.documents);
  };

  // if (loading) return null;
  // if (unauthorized) return <InvalidModal isIta={isIta} />;
  // if (typeMismatch) return <MismatchModal isIta={isItaFallback} />;
  // if (!urlParamsAml) return <InvalidModal isIta={isIta} />;

  if (loading) return null;
  if (unauthorized || !urlParamsAml) {
    clearAuthToken();
    window.location.reload();
    return null;
  }
  if (typeMismatch) return <MismatchModal isIta={isItaFallback} />;

  return (
    <>
      {/* Overlay generazione PDF */}
      {generatingPdf && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0,0,0,0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 2000,
          }}
        >
          <CircularProgress sx={{ color: "#fff" }} />
        </div>
      )}

      <div
        style={{ display: "flex", flexDirection: "column", height: "100vh" }}
      >
        <div style={{ position: "fixed", top: 0, width: "100%", zIndex: 1000 }}>
          <Header isIta={isIta} />
          {currentStep !== 5 && currentStep !== 6 && (
            <HeaderStepPractice
              currentStep={currentStep}
              steps={
                isIta
                  ? ["Cliente", "Esecutore", "Titolare eff.", "Prestazione"]
                  : ["Client", "Executor", "UBO", "Services"]
              }
            />
          )}
        </div>

        <div
          ref={containerRef}
          style={{
            flex: 1,
            overflowY: "auto",
            marginTop:
              currentStep === 5 || currentStep === 6 ? "100px" : "180px",
            marginBottom: "80px",
          }}
        >
          {urlParamsAml === "fisico-ita" || urlParamsAml === "fisico-eng" ? (
            <AML_FISICO
              goToStep={goToStep}
              documents={documents}
              errors={fieldErrors}
              currentStep={currentStep}
              aml={aml}
              urlParamsAml={urlParamsAml}
              handleAmlUpdate={handleAmlUpdate}
              caseData={caseData}
              languange={isIta ? "ITA" : "ENG"}
              uid={uid}
              refreshDocuments={refreshDocuments}
              saveAmlData={saveAmlData}
            />
          ) : urlParamsAml === "giuridico-ita" ||
            urlParamsAml === "giuridico-eng" ? (
            <AML_GIURIDICO
              saveAml={saveAml}
              goToStep={goToStep}
              documents={documents}
              urlParamsAml={urlParamsAml}
              errors={fieldErrors}
              currentStep={currentStep}
              aml={aml}
              handleAmlUpdate={handleAmlUpdate}
              caseData={caseData}
              languange={isIta ? "ITA" : "ENG"}
              uid={uid}
              refreshDocuments={refreshDocuments}
              saveAmlData={saveAmlData}
            />
          ) : null}
        </div>

        <div
          style={{
            position: "fixed",
            bottom: 0,
            left: 0,
            width: "100%",
            padding: "15px 0px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <BoxButton
            urlParamsAml={urlParamsAml}
            viewBoxButton={false}
            currentStep={currentStep}
            onNext={handleNext}
            onPrevious={handlePrevious}
            startAgain={openModalResetPractice}
            modality="aml"
          />
        </div>
      </div>

      {error && (
        <Box
          sx={{
            width: { xs: "90%", sm: "350px" },
            position: "fixed",
            bottom: "-15px",
            left: "50%",
            backgroundColor: "#fff",
            transform: "translate(-50%, -50%)",
            zIndex: 1000,
            padding: "12px 10px",
            borderRadius: "10px",
            boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.2)",
            fontSize: "14px",
          }}
        >
          {(fieldErrors || []).includes("titolari") ? (
            <PopupError
              copyError={
                isIta
                  ? "È necessario aggiungere almeno un titolare"
                  : "It is necessary to add at least one owner"
              }
              closePopupError={closePopupError}
            />
          ) : (fieldErrors || []).includes("aml_signed_missing") ? (
            <PopupError
              copyError={
                isIta
                  ? "Devi caricare il modello AML firmato"
                  : "You must upload the signed AML document"
              }
              closePopupError={closePopupError}
            />
          ) : (
            <PopupError
              copyError={
                isIta
                  ? "Uno o più campi obbligatori sono mancanti"
                  : "One or more required fields are missing"
              }
              closePopupError={closePopupError}
            />
          )}
        </Box>
      )}

      {resetPractice && (
        <Modal open={resetPractice} onClose={handleCloseResetPracticeModal}>
          <ModalError
            modalInfo={true}
            title={isIta ? "Azzera dati" : "Reset data"}
            descriptionOne={
              isIta
                ? "Attenzione! L'azzeramento dei dati è un'azione irreversibile con cui vengono cancellati definitivamente tutti i progressi della bozza."
                : "Warning! Resetting the data is an irreversible action that will permanently delete all draft progress."
            }
            descriptionTwo={
              isIta
                ? "Sei sicuro di voler procedere?"
                : "Are you sure you want to proceed?"
            }
            copyButton={isIta ? "Sì, azzera" : "Yes, reset data"}
            confirm={startAgain}
            onClose={handleCloseResetPracticeModal}
          />
        </Modal>
      )}

      {/* Modale conferma dati — step 5 → step 6 + generazione PDF */}
      <Modal open={modalOpen} onClose={handleCloseModal}>
        <ModalConferma
          title={isIta ? "Conferma dati" : "Confirm data"}
          descriptionOne={
            isIta
              ? "Attenzione! Confermando i dati verrà generato il modulo anti-riciclaggio definitivo da firmare."
              : "Warning! By confirming the data, the final anti-money laundering form to be signed will be generated."
          }
          descriptionTwo={
            isIta
              ? "Sei sicuro che tutti i dati siano corretti?"
              : "Are you sure all the data is correct?"
          }
          copyButton={isIta ? "Sì, procedi" : "Yes, proceed"}
          confirm={async () => {
            setModalOpen(false);

            // Elimina l'eventuale PDF firmato precedente
            const signedDoc = (documents || []).find(
              (d) => d.type === "client_aml_signed",
            );
            if (signedDoc) {
              try {
                await service.deleteDocument(signedDoc.id);
                await refreshDocuments();
              } catch (err) {
                console.error("Errore eliminazione doc firmato:", err);
              }
            }

            await generatePdf();
            await saveAml(6);
            setCurrentStep(6);
          }}
          onClose={handleCloseModal}
        />
      </Modal>

      {/* Modale conferma invio — step 6 → submit finale */}
      <Modal open={confirmAmlModal} onClose={() => setConfirmAmlModal(false)}>
        <ModalConfermaAml
          title={isIta ? "Conferma invio modulo" : "Confirm form submission"}
          descriptionOne={
            isIta
              ? "Attenzione! L'invio è un'azione irreversibile con cui si inoltra automaticamente il modulo anti-riciclaggio all'amministrazione LEXIA."
              : "Warning! Submission is an irreversible action that will automatically send the anti-money laundering form to the LEXIA administration."
          }
          descriptionTwo={
            isIta
              ? "Sei sicuro di voler procedere?"
              : "Are you sure you want to proceed?"
          }
          copyButtonClose={isIta ? "Chiudi" : "Close"}
          copyButtonConfirm={isIta ? "Sì, invia" : "Yes, send"}
          onClose={() => setConfirmAmlModal(false)}
          confirm={async () => {
            await sendFormAml();
            setConfirmAmlModal(false);
            setSuccessAmlModal(true);
          }}
        />
      </Modal>

      <Modal open={successAmlModal} onClose={() => {}}>
        <ModalSuccessAml isIta={isIta} status={caseData?.case?.status} />
      </Modal>
    </>
  );
};

export default AmlPage;
